'use client';

/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react';
import {
  TCConsumerProps,
  TCContext,
  TCContextNames,
  TCContextState,
  TCContextValue,
  TCNewState,
  TCProviderProps,
  TCStateKey,
  TCUseContextReturn,
} from './CustomContext.types';

const Contexts: Partial<{
  [TContextName in TCContextNames]: TCContext<TContextName>;
}> = {};

const prepareContextDisplayName = (name: string): string =>
  name.replace(/\w+/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase()) +
  'Context';

const getContext = <TContextName extends TCContextNames>(
  name: TContextName
): TCContext<TContextName> => {
  type TContext = TCContext<TContextName>;
  type TContextValue = TCContextValue<TContextName>;

  if (!Contexts[name]) {
    const NewContext: TContext = createContext<TContextValue>(
      {} as TContextValue
    );
    NewContext.displayName = prepareContextDisplayName(name);
    // @ts-ignore
    Contexts[name] = NewContext;
  }

  return Contexts[name] as TContext;
};

export const CustomContextProvider = <
  TContextName extends TCContextNames = TCContextNames,
>({
  name,
  children = null,
  initialState,
}: TCProviderProps<TContextName>): JSX.Element => {
  type TState = TCContextState<TContextName>;
  type TStateKey = TCStateKey<TContextName> | undefined;
  type TNewState = TCNewState<TState>;

  const Context: TCContext<TContextName> = getContext<TContextName>(name);

  const [state, dispatch] = useReducer(
    (
      prevState: TState,
      action: { newState: TNewState | any; stateKey?: TStateKey }
    ) => {
      let { newState, stateKey } = action;

      if (typeof newState === 'function') {
        if (stateKey) {
          const value = prevState[stateKey] as any;
          newState = { [stateKey]: newState(value) } as Partial<TState>;
        } else {
          newState = newState(prevState);
        }
      } else if (stateKey) {
        newState = { [stateKey]: newState } as Partial<TState>;
      }

      return { ...prevState, ...newState };
    },
    { ...initialState }
  );

  const setState = useCallback(
    (newState: TNewState, stateKey?: TStateKey) =>
      dispatch({ newState, stateKey }),
    [dispatch]
  );

  const value: TCContextValue<TContextName> = {
    initialState,
    state,
    ...state,
    setState,
  } as const;

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const CustomContextConsumer = <
  TContextName extends TCContextNames = TCContextNames,
>({
  name,
  children,
}: TCConsumerProps<TContextName>): JSX.Element => {
  const Context: TCContext<TContextName> = getContext<TContextName>(name);
  return <Context.Consumer>{children as any}</Context.Consumer>;
};

export function useCustomContext<
  TStateKey extends TCStateKey<TContextName> | undefined,
  TContextName extends TCContextNames = TCContextNames,
>(
  name: TContextName,
  stateKey?: TStateKey
): TCUseContextReturn<TContextName, TStateKey> {
  type TReturn = TCUseContextReturn<TContextName, TStateKey>;

  const contextValue: TCContextValue<TContextName> = useContext(
    getContext<TContextName>(name)
  );

  const stateValue = stateKey
    ? contextValue?.state?.[stateKey as TCStateKey<TContextName>]
    : undefined;

  return (useMemo(() => {
    return stateKey
      ? [stateValue, (params: any) => contextValue.setState(params, stateKey)]
      : undefined;
  }, [stateKey, stateValue]) ?? contextValue) as TReturn;
}
